import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash2, Loader2, PieChart } from "lucide-react";

export function ShareOfVoice() {
  const { orgId } = useOrgPermissions();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [newCompetitor, setNewCompetitor] = useState("");

  const { data: competitors = [], isLoading: loadingComp } = useQuery({
    queryKey: ["competitors", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase
        .from("competitor_tracking" as any)
        .select("*")
        .eq("org_id", orgId!)
        .eq("is_active", true)
        .order("competitor_name") as any);
      return (data ?? []) as any[];
    },
  });

  const { data: snapshots = [], isLoading: loadingSnaps } = useQuery({
    queryKey: ["sov-snapshots", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase
        .from("sov_snapshots" as any)
        .select("*, competitor_tracking(competitor_name)")
        .eq("org_id", orgId!)
        .order("period_start", { ascending: false })
        .limit(30) as any);
      return (data ?? []) as any[];
    },
  });

  const addMutation = useMutation({
    mutationFn: async () => {
      if (!newCompetitor.trim()) throw new Error("Enter a competitor name");
      const { error } = await (supabase
        .from("competitor_tracking" as any)
        .insert({ org_id: orgId, competitor_name: newCompetitor.trim(), keywords: [] }) as any);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Competitor added." });
      setNewCompetitor("");
      qc.invalidateQueries({ queryKey: ["competitors", orgId] });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase
        .from("competitor_tracking" as any)
        .update({ is_active: false })
        .eq("id", id) as any);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["competitors", orgId] }),
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-5">
        {/* Competitors panel */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Tracked Competitors</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <Input
                placeholder="Competitor name…"
                value={newCompetitor}
                onChange={e => setNewCompetitor(e.target.value)}
                onKeyDown={e => e.key === "Enter" && addMutation.mutate()}
              />
              <Button size="sm" onClick={() => addMutation.mutate()} disabled={addMutation.isPending || !newCompetitor.trim()}>
                {addMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              </Button>
            </div>
            {loadingComp ? (
              <div className="flex items-center gap-2 text-muted-foreground text-sm py-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
              </div>
            ) : competitors.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">No competitors tracked yet.</p>
            ) : (
              <div className="space-y-1.5">
                {competitors.map((c: any) => (
                  <div key={c.id} className="flex items-center justify-between text-sm py-1 border-b border-border last:border-0">
                    <span className="font-medium">{c.competitor_name}</span>
                    <button
                      onClick={() => deleteMutation.mutate(c.id)}
                      className="text-muted-foreground hover:text-red-500"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* SoV snapshot summary */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <PieChart className="w-4 h-4" /> Share of Voice Snapshots
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingSnaps ? (
              <div className="flex items-center gap-2 text-muted-foreground text-sm py-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
              </div>
            ) : snapshots.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">No snapshots available yet. Snapshots are generated automatically by the SOV compute job.</p>
            ) : (
              <div className="space-y-3">
                {snapshots.slice(0, 8).map((snap: any) => {
                  const totalMentions = (snap.org_mentions ?? 0) + (snap.competitor_mentions ?? 0);
                  const orgPct = totalMentions === 0 ? 0 : Math.round(((snap.org_mentions ?? 0) / totalMentions) * 100);
                  return (
                    <div key={snap.id}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="font-medium">{(snap.competitor_tracking as any)?.competitor_name ?? "—"}</span>
                        <span className="text-muted-foreground">{new Date(snap.period_start).toLocaleDateString("en-GB", { month: "short", day: "numeric" })} – {new Date(snap.period_end).toLocaleDateString("en-GB", { month: "short", day: "numeric" })}</span>
                      </div>
                      <div className="flex h-4 rounded-full overflow-hidden gap-px">
                        <div className="bg-[#0D2240] flex items-center justify-center text-[9px] text-white font-bold" style={{ width: `${orgPct}%` }}>
                          {orgPct > 10 ? `${orgPct}%` : ""}
                        </div>
                        <div className="bg-slate-300 flex items-center justify-center text-[9px] text-slate-700 font-bold" style={{ width: `${100 - orgPct}%` }}>
                          {(100 - orgPct) > 10 ? `${100 - orgPct}%` : ""}
                        </div>
                      </div>
                      <div className="flex justify-between text-[10px] text-muted-foreground mt-0.5">
                        <span>You: {snap.org_mentions}</span>
                        <span>Competitor: {snap.competitor_mentions}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
